import React from 'react';
import './PythonAuditSection.scss';
import PRContributions from "../../assets/images/PRContributionsStatistics.jpg"
import LeftArrowIcon from "../../assets/icons/left-arrow-icon.svg"

const PythonAuditSection = () => {
  return (
      <section id="python-audit" className="python-audit-section">
        <div className="container">
          {/* Back Button */}
          <div className="back-button-wrapper">
            <button className="back-button" onClick={() => window.history.back()}>
              <img alt="back-btn" className="back-btn-img" src={LeftArrowIcon}/> Back
            </button>
          </div>

          {/* Hero Section */}
          <div className="section-card hero-section">
            <h2 className="section-title">Enterprise Python Code Audit & Architecture Review</h2>
            <p className="section-description">Top-35 CPython Contributor | 775+ Merged PRs | EU-Compliant & Secure</p>
            <p className="section-subtitle">The same rigorous standards that power Python itself, applied to your codebase</p>
          </div>

          <div className="columns grid-layout">

            {/* Full Row: From Good Code to Excellence */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">From Good Code to Excellence: </h3>
                <h4 className="sub-title-2">The Standards That Shape Python, Now Available for Your Project.</h4>
                <div className="section-description">
                  <p><strong>Problem: </strong>“Your Python project is critical to your business,
                    but are you truly confident it’s ready for future challenges?
                    Standard practices and internal reviews often miss subtle architectural flaws,
                    hidden performance bottlenecks, and suboptimal approaches.
                    Without a deep understanding of Python’s inner workings
                    and the best practices applied at its core, your code, while functional,
                    may be far from its true potential, hindering development
                    and complicating long-term maintenance.”
                  </p>
                  <p><strong>Solution: </strong>“We bring the development standards of CPython directly to your project.
                    Our expert, one of the key contributors to the Python language itself,
                    will audit your codebase with the same rigor applied to Python’s core.
                    This isn’t just about finding bugs; it’s about instilling the mindset
                    and practices that build high-performance, robust, and maintainable systems.
                    You won’t just receive a list of recommendations,
                    but a strategic development plan for your product
                    from someone who actively shapes the Python language
                    as a CPython Core Developer, ensuring the long-term stability
                    and efficiency of your Python systems.
                  </p>
                </div>
              </div>
            </div>

            {/* Full Row: Expertise That Shapes Python */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Expertise That Shapes Python</h3>
                <div className="git-pr-overview">
                  <div>
                    <img alt="git-pr-overview" src={PRContributions}/>
                  </div>
                </div>
                <h4 className="sub-title-2"><strong>Our service is powered by the unparalleled expertise of a CPython
                  core team member, bringing the same rigorous development standards directly to your codebase.</strong>
                </h4>
                <div className="highlight-box">
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">1. Direct Impact on Python’s Core (775+ PRs)</h4>
                    <ul className="info-list">
                      <li>775+ Pull Requests (PRs) merged into CPython since 2020 demonstrate a continuous, deep
                        involvement in the language’s evolution. This isn’t just contribution; it’s shaping Python’s
                        future.
                      </li>
                      <li>Over 45,000 lines of code changes within the core Python repository. This active participation
                        ensures an intimate understanding of the language’s intricacies and best practices.
                      </li>
                      <li>Focus on Critical Areas: Significant contributions to async (26 PRs) and typing (70 PRs), as
                        well as over 300 bug fixes, highlight expertise in modern Python development and code stability.
                      </li>
                    </ul>
                  </div>
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">2. Endorsed by Python Community Leaders</h4>
                    <ul className="info-list">
                      <li>
                        Official CPython Core Team Member: Our expert’s status on python.org/dev/committers
                        confirms their official role in the Python core development team.
                      </li>
                      <li>
                        Author of wemake-python-styleguide:
                        Creator of a widely adopted Python linter (2,7k⭐ stars on GitHub),
                        demonstrating a commitment to high code quality standards applied in real-world projects.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Full Row: Build a Foundation of Quality for Your Python Code */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Build a Foundation of Quality for Your Python Code</h3>
                <h4 className="sub-title-2 grey">A deep analysis of your project by a core CPython developer isn’t just
                  a review – it’s an investment in your product’s stability and future.”</h4>
                <ul className="info-list">
                  <li><strong>Identify Hidden Issues: </strong>We help uncover critical bugs, technical debt, and
                    performance bottlenecks that could slow down your product or lead to costly failures.
                  </li>
                  <li><strong>Apply CPython Best Practices: </strong>Your code will be evaluated against the very
                    standards used to develop the Python language itself, ensuring reliability and scalability.
                  </li>
                  <li><strong>Receive Expert-Level Recommendations: </strong>Beyond just a list of errors, you’ll get
                    clear, actionable recommendations from a specialist who actively shapes Python.
                  </li>
                  <li><strong>Lay the Groundwork for Growth & Security: </strong>Understand how to improve your
                    architecture, code quality, and security posture, ensuring your project is prepared for future
                    challenges and scaling with confidence.
                  </li>
                </ul>
                <h4 className="sub-title-3">Start with an audit that sets the right direction for your team and provides
                  confidence in the quality of your Python solution from day one.</h4>
              </div>
            </div>

            {/* Full Row: Our Process: From Inquiry to Deep Code Audit */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Our Process: From Inquiry to Deep Code Audit</h3>
                <h4 className="sub-title-2">We have developed a transparent and efficient process to provide you with
                  seamless access to world-class expertise. Every step is designed for your confidence and maximum
                  value.</h4>
                <h4 className="sub-title-2"><strong>5 Steps to Flawless Python Code:</strong></h4>

                <div className="highlight-box">
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">Step 1: Initial Inquiry & Discussion</h4>
                    <ul className="info-list">
                      <li><strong>What happens:</strong> You fill out a simple form on our website or contact us
                        directly. Our project manager will reach out for a brief introductory call to discuss your
                        goals, challenges, and project scope (e.g., estimated lines of code, application type).
                      </li>
                      <li><strong>Your Benefit:</strong> We identify how to best assist you and propose an optimal
                        service package tailored to your needs.
                      </li>
                      <li>Timeline: Completed within 1-2 business days.</li>
                    </ul>
                  </div>
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">Step 2: Scope Definition & Agreement Signing</h4>
                    <ul className="info-list">
                      <li><strong>What happens:</strong> We prepare a detailed commercial proposal based on the initial
                        discussion, followed by signing an NDA and service contract under Finnish/EU law upon your
                        approval.
                      </li>
                      <li><strong>Your Benefit:</strong> Your data is fully protected legally, with clear cooperation
                        terms and reliable Finnish jurisdiction ensuring compliance with EU standards.
                      </li>
                      <li>Timeline: Completed within 2-3 business days.</li>
                    </ul>
                  </div>
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">Step 3: Secure Code Access & Preparation</h4>
                    <ul className="info-list">
                      <li><strong>What happens:</strong> After signing documents and receiving 50% upfront payment, you
                        provide secure access to your code repository (e.g., via temporary GitHub/GitLab credentials),
                        and our manager ensures the expert has all necessary resources.
                      </li>
                      <li><strong>Your Benefit:</strong> Your code remains secure with strict confidentiality and
                        intellectual property protection through secure communication channels and protocols.
                      </li>
                      <li>Timeline: Completed within 1 business day.</li>
                    </ul>
                  </div>
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">Step 4: Deep Code Analysis by the Expert</h4>
                    <ul className="info-list">
                      <li><strong>What happens:</strong> Our expert conducts a thorough code analysis using CPython
                        development principles, identifying architectural issues, performance bottlenecks,
                        vulnerabilities, and optimization opportunities.
                      </li>
                      <li><strong>Your Benefit:</strong> A strategic audit by a respected Python open-source contributor
                        enhances code quality, performance, and stability.
                      </li>
                      <li>Timeline: Varies by project scope, from 2 business days to 2 weeks, depending on the selected
                        package.
                      </li>
                    </ul>
                  </div>
                  <div className="highlight-sub-box">
                    <h4 className="highlight-title">Step 5: Report Delivery & Q&A Session</h4>
                    <ul className="info-list">
                      <li><strong>What happens:</strong> You receive a detailed PDF report with identified issues,
                        criticality classification, and remediation recommendations, including pull request examples for
                        select packages, followed by a Q&A session with the expert and final 50% payment.
                      </li>
                      <li><strong>Your Benefit:</strong> A strategic action plan improves your Python application’s
                        quality, performance, and reliability, with direct expert access to clarify questions.
                      </li>
                      <li>Timeline: Completed within 1-2 business days after analysis.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Full Row: Our Guarantees */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Our Guarantees:</h3>
                <ul className="info-list">
                  <li><strong>Complete Confidentiality: </strong>Always under NDA, compliant with GDPR and European
                    legislation.
                  </li>
                  <li><strong>Finnish Jurisdiction: </strong>Your protection and transaction transparency are ensured by
                    Finland’s reliable legal system.
                  </li>
                  <li><strong>Transparent Payments: </strong>Staged payment model (50% upfront, 50% upon results) for
                    your peace of mind.
                  </li>
                  <li><strong>Quality Assurance: </strong>We guarantee that the analysis level will meet the highest
                    standards applied in CPython development.
                  </li>
                </ul>
              </div>
            </div>

            {/* Full Row: Pricing */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Pricing</h3>
                <h4 className="sub-title-2"><strong>Review Models & Pricing Guidelines</strong></h4>
                <h4 className="sub-title-2"><strong>Tailored code audits, adaptable to your codebase and business
                  goals.</strong></h4>
                <h4 className="sub-title-3">We offer flexible code review formats depending on the size, complexity, and
                  needs of your project.
                  Below are typical examples - final scope and pricing are always customized after the initial
                  discussion.</h4>
                <div className="pricing-table">
                  <table>
                    <thead>
                    <tr>
                      <th>Package</th>
                      <th>Fast-Track Review</th>
                      <th>Scale-Up Audit</th>
                      <th>Continuous Quality Retainer</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                      <td>Price</td>
                      <td>€4,000</td>
                      <td>€9,000</td>
                      <td>€5,000 / month</td>
                    </tr>
                    <tr>
                      <td>Codebase Size</td>
                      <td>Up to 15,000 lines</td>
                      <td>Up to 50,000 lines</td>
                      <td>Up to 8 hours expert time/mo</td>
                    </tr>
                    <tr>
                      <td>Duration</td>
                      <td>2-5 business days</td>
                      <td>1-2 weeks</td>
                      <td>Ongoing</td>
                    </tr>
                    <tr>
                      <td>Deliverables</td>
                      <td>
                        - Comprehensive PDF report<br/>
                        - All critical recommendations<br/>
                        - 180-min Q&A session with the expert
                      </td>
                      <td>
                        - Comprehensive PDF report<br/>
                        - Sample pull requests with fixes<br/>
                        - CI/CD improvement roadmap<br/>
                        - 3 x 180-min strategy sessions with the expert
                      </td>
                      <td>
                        - Ongoing review of new PRs and architecture<br/>
                        - Regular expert consultations<br/>
                        - Priority support for critical issues
                      </td>
                    </tr>
                    <tr>
                      <td>Process</td>
                      <td>
                        - Remote, under NDA<br/>
                        - Secure, EU-compliant workflow<br/>
                        - Finnish legal entity guarantee
                      </td>
                      <td>
                        - Remote, under NDA<br/>
                        - Secure, EU-compliant workflow<br/>
                        - Finnish legal entity guarantee
                      </td>
                      <td>
                        - Remote, under NDA<br/>
                        - Secure, EU-compliant workflow<br/>
                        - Finnish legal entity guarantee
                      </td>
                    </tr>
                    <tr>
                      <td>Best for</td>
                      <td>Quick insight into code quality & urgent issues</td>
                      <td>Deep, strategic audit of complex projects</td>
                      <td>Teams seeking ongoing, enterprise-level code quality oversight</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p className="sub-title-3">
                  All packages include:
                </p>
                <ul className="info-list">
                  <li>Audit and recommendations by a CPython contributor and Python expert</li>
                  <li>Full confidentiality (strict NDA / Finnish jurisdiction)</li>
                  <li>Transparent process, staged payments, and clear milestones</li>
                  <li>Quality Assurance: Our commitment to providing deep and actionable analysis.</li>
                </ul>
                <p className="sub-title-3">
                  <strong>Need a tailored solution?</strong>
                </p>
                <p className="section-description">
                  Contact us to discuss custom engagement models for larger codebases or high-complexity projects.
                </p>
                <p className="section-description contact-email">
                  e-mail: <span className="link">hello@kanzu.fi</span><br/>
                </p>
              </div>
            </div>

            {/* Full Row: The Expert */}
            <div className="grid-item full-width">
              <div className="section-card">
                <div className="expert-section-header">
                  <h3 className="sub-title">The Expert: Nikita Sobolev</h3>
                  <p className="sub-title-3">
                    At the core of our service is the unparalleled expertise of Nikita Sobolev,
                    a distinguished member of the CPython core development team.
                    His contributions don't just follow Python's evolution;
                    they actively shape it, earning recognition from the highest echelons
                    of the open-source world.
                  </p>
                </div>

                <div className="expert-section-body">
                  <div className="highlight-box">
                    <h4 className="highlight-title"><strong>Key Contributions & Achievements:</strong></h4>
                    <ul className="info-list">
                      <li><strong>CPython Core Developer: </strong>An officially recognized core developer for the
                        Python programming language, listed on python.org/dev/committers. This status is granted through
                        rigorous selection by the core team.
                      </li>
                      <li>
                        <strong>Top Contributor to CPython: </strong>
                        Ranked among the top 35 individual contributors to CPython by Pull Request count.
                        <ul className="info-list">
                          <li><a href="https://github.com/python/cpython/graphs/contributors">775+ merged Pull Requests
                            (PRs) since March 2020</a>: A testament to continuous, impactful engagement with the
                            language's core. These contributions range from critical bug fixes and performance
                            optimizations to fundamental language enhancements.
                          </li>
                          <li><strong>45,000+ lines of code changes: </strong>Direct involvement in refining and
                            expanding the Python codebase.
                          </li>
                        </ul>
                      </li>
                      <li><strong>GitHub Star & Arctic Code Vault Contributor:</strong>
                        <ul className="info-list">
                          <li><strong>GitHub Star Alumni:</strong>
                            In 2020, Nikita was invited to the prestigious GitHub Stars program, recognizing his
                            significant influence and leadership within the global developer community. His profile is
                            listed among GitHub Stars alumni.
                          </li>
                          <li><strong>Arctic Code Vault Contributor:</strong>
                            His substantial contributions to open-source have been deemed significant enough to be
                            permanently archived in the GitHub Arctic Code Vault (2020), preserving his work for future
                            generations.
                          </li>
                        </ul>
                      </li>
                      <li><strong>Author of <a
                          href="https://github.com/wemake-services/wemake-python-styleguide">wemake-python-styleguide:</a>
                      </strong><br/>The creator of wemake-python-styleguide – lauded as <strong>“The strictest and most
                        opinionated Python linter ever.”</strong> This flagship project, implemented as a Flake8 plugin
                        compatible with Ruff, has garnered <strong>~2.7k stars and ~400 forks on <a
                            href="https://github.com/sobolevn">GitHub.</a></strong> It stands as a testament to his
                        unwavering commitment to high code quality and best practices.
                      </li>
                    </ul>
                  </div>

                  <div className="highlight-box">
                    <h4 className="highlight-title">Why This Matters For Your Code:</h4>
                    <h4 className="highlight-description">Nikita's deep involvement in CPython development and his broad
                      recognition in the open-source world mean he possesses an unmatched understanding of:</h4>
                    <ul className="info-list">
                      <li><strong>Python's Internal Mechanics: </strong>How Python truly works under the hood, enabling
                        identification of subtle performance bottlenecks and architectural flaws often missed by others.
                      </li>
                      <li><strong>Best Practices & Future Trends: </strong>Insights into the latest language features,
                        optimal patterns, and upcoming changes, ensuring your codebase is not just clean, but
                        future-proof. His work on wemake-python-styleguide directly translates into practical,
                        actionable advice for maintaining enterprise-grade code quality.
                      </li>
                      <li><strong>Robustness & Stability: </strong>A meticulous approach to code quality, mirroring the
                        standards required for the language itself, leading to more reliable and secure applications.
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="expert-section-footer">
                  <h3 className="sub-title-2"><strong>This is not just a code review; it's a strategic audit
                    from a developer whose work is literally etched into the history of Python and recognized
                    globally.</strong></h3>
                </div>
              </div>

            </div>

            {/* Full Row: Our Role*/}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Our Role: Your Trusted Operational Partner</h3>
                <h4 className="sub-title-2">At Kanzu Oy, we act as your dedicated facilitator, providing direct and
                  secure access to exceptional Python code review expertise.</h4>
                <ul className="info-list">
                  <li><strong>Expertise without Barriers: </strong>We partner with Nikita Sobolev, a CPython core
                    developer and maintainer, to deliver world-class code review services. All technical assessments are
                    conducted exclusively by the expert; our company ensures seamless process management and operational
                    excellence.
                  </li>
                  <li><strong>Transparent Processes: </strong>From the initial scoping call to the delivery of your
                    audit report, we handle all communication, contract management, scheduling, and legal frameworks (EU
                    & Finnish jurisdiction). You focus on your code—while we take care of privacy, compliance, and
                    logistics.
                  </li>
                  <li><strong>Independent Assessment: </strong>The expert acts as an independent reviewer of your
                    repositories. We guarantee that all findings and recommendations are objective and based on proven
                    open source methodologies that underpin Python itself.
                  </li>
                  <li><strong>Security & Confidentiality: </strong>We prioritize security with strict NDA policies,
                    GDPR-compliant operations, and trusted payment solutions. All reviews are performed under
                    transparent agreements, protecting your intellectual property at every stage.
                  </li>
                </ul>
                <h4 className="sub-title-2"><strong>We provide an independent, world-class perspective - delivering clarity, confidence, and actionable results for your most critical Python codebases.</strong></h4>
              </div>
            </div>

            {/* Two Columns: Example Cases & Technologies */}
            {/* Example Cases (3/4 width) */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Technologies & Focus Areas</h3>
                <div className="tech-tags">
                  {['Python 3.9+', 'Django', 'FastAPI', 'Flask', 'Postgres', 'Redis', 'Docker', 'GitHub Actions', 'CI/CD pipelines', 'Open source projects', 'SaaS systems', 'Static analysis', 'Code health', 'Software design'].map((tech) => (
                      <span key={tech} className="tech-tag">{tech}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Full Row: Get in Touch */}
            <div className="grid-item full-width">
              <div className="section-card">
                <h3 className="sub-title">Get in Touch</h3>
                <p className="section-description">
                  If you need clarity, confidence, and quality in your codebase — reach out. We’ll guide you through the
                  process and help you turn tech debt into technical advantage.
                </p>
                <p className="section-description contact-email">
                  Contact us at <span className="link">hello@kanzu.fi</span><br/>
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="section-card start-button">
            <h3 className="sub-title">Let's Build Better Code. Together.</h3>
            <a href="/#contact" className="btn btn-primary">Contact Us Today</a>
          </div>
        </div>
      </section>
  );
};

export default PythonAuditSection;